import { Form, FormProps, Switch, TableProps } from '@pankod/refine-antd';
import { ActionCell } from 'components/ActionCell';
import { TitleFilter } from 'components/TitleFilter';
import { ListPageLayout } from 'components/layout/ListPageLayout';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganizationOptions } from 'hooks/useOrganizationOptions';
import { httpClient } from 'services/httpClient';
import { buildTableColumn } from 'utils';
import { buildUserRoleOptions } from 'utils/buildUserRoleOptions';
import { Users } from 'utils/hasura';

const { useForm } = Form;

type UserFilters = {
  orgId?: string;
  title?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
};

export const UserList: React.FC<any> = () => {
  const { t } = useTranslation();

  const [form] = useForm();
  const [users, setUsers] = useState<Users[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<{
    current: number;
    pageSize: number;
    total: number;
  }>({
    current: 1,
    pageSize: 12,
    total: 0
  });

  type SingleSorter = {
    field?: React.Key | readonly React.Key[];
    order?: 'ascend' | 'descend';
  };
  type Sorter = SingleSorter | SingleSorter[];

  const [sorter, setSorter] = useState<Sorter>([]);

  const [filters, setFilters] = useState<UserFilters>({});

  const { selectProps: selectOrganizationsProps } = useOrganizationOptions();

  const fetchUsers = useCallback(
    async (
      page: number,
      pageSize: number,
      currentSorter: Sorter,
      currentFilters: UserFilters
    ) => {
      setLoading(true);

      try {
        const sortableFieldMap: Record<string, string> = {
          id: 'id',
          email: 'email',
          role: 'role',
          title: 'title',
          firstName: 'firstName',
          lastName: 'lastName',
          createdAt: 'createdAt',
          orgId: 'orgName'
        };

        const sorterArray: SingleSorter[] = Array.isArray(currentSorter)
          ? currentSorter
          : currentSorter.field
          ? [currentSorter]
          : [];

        const sortParts = sorterArray
          .filter(s => s.field && s.order)
          .map(s => {
            const apiField =
              sortableFieldMap[s.field as string] || s.field!.toString();
            return s.order === 'descend' ? `-${apiField}` : apiField;
          });

        const sortParam =
          sortParts.length > 0 ? sortParts.join(',') : undefined;

        const response = await httpClient.get('/admin/users', {
          params: {
            page,
            limit: pageSize,
            ...(sortParam ? { sort: sortParam } : {}),
            ...currentFilters
          }
        });

        setUsers(response.data.data || []);
        setPagination(prev => ({
          ...prev,
          current: page,
          pageSize,
          total: response.data.paging.total || 0
        }));
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchUsers(pagination.current, pagination.pageSize, sorter, filters);
  }, [fetchUsers, pagination.current, pagination.pageSize, sorter, filters]);

  const handleTableChange: TableProps<Users>['onChange'] = (
    nextPagination,
    _tableFilters,
    nextSorter
  ) => {
    // Only update state; the effect below will trigger a single fetch.
    setPagination(prev => ({
      ...prev,
      current: nextPagination.current || 1,
      pageSize: nextPagination.pageSize || prev.pageSize
    }));

    const sorterArray: SingleSorter[] = Array.isArray(nextSorter)
      ? nextSorter
          .filter(s => s.field && s.order)
          .map(s => ({
            field: s.field,
            order:
              s.order === 'ascend' || s.order === 'descend'
                ? s.order
                : undefined
          }))
      : nextSorter.field && nextSorter.order
      ? [
          {
            field: nextSorter.field,
            order:
              nextSorter.order === 'ascend' || nextSorter.order === 'descend'
                ? nextSorter.order
                : undefined
          }
        ]
      : [];

    setSorter(sorterArray);
  };

  const handleSearch = (values: any) => {
    const {
      organization_id: orgId,
      firstName,
      title,
      lastName,
      email,
      role
    } = values;

    const trim = (value?: string) => value?.trim() || undefined;

    setFilters({
      orgId,
      title: trim(title),
      firstName: trim(firstName),
      lastName: trim(lastName),
      email: trim(email),
      role
    });

    setPagination(prev => ({
      ...prev,
      current: 1
    }));
  };

  const toggleActive = async (id: number, isActive: boolean) => {
    try {
      await httpClient.patch(`/admin/users/${id}`, { isActive });
    } finally {
      fetchUsers(pagination.current, pagination.pageSize, sorter, filters);
    }
  };

  const columns: TableProps<Users>['columns'] = [
    buildTableColumn(
      <TitleFilter
        name="organization_id"
        title={t('pages.users.organization')}
        filterType="dropdown"
        selectProps={selectOrganizationsProps}
      />,
      'orgId',
      true,
      (_, record) => record.organization.name
    ),
    buildTableColumn(
      <TitleFilter name="title" title={t('pages.users.title')} />,
      'title'
    ),
    buildTableColumn(
      <TitleFilter name="firstName" title={t('pages.users.firstName')} />,
      'firstName'
    ),
    buildTableColumn(
      <TitleFilter name="lastName" title={t('pages.users.lastName')} />,
      'lastName'
    ),
    buildTableColumn(
      <TitleFilter name="email" title={t('pages.users.email')} />,
      'email'
    ),
    buildTableColumn(
      <TitleFilter
        name="role"
        title={t('pages.users.role')}
        filterType="dropdown"
        options={buildUserRoleOptions(t)}
      />,
      'role',
      true,
      (_, record) => t(`pages.users.roles.${record.role.toLocaleLowerCase()}`)
    ),
    buildTableColumn(
      t('pages.productList.active'),
      'isActive',
      false,
      (value: boolean, record) => (
        <Switch
          checked={!!value}
          onChange={checked => toggleActive(record.id, checked)}
        />
      )
    ),
    buildTableColumn('', 'id', false, (_, record) => (
      <ActionCell id={record.id} actions={['edit']} />
    ))
  ];

  const tableProps: TableProps<Users> = {
    dataSource: users,
    loading,
    pagination,
    columns,
    onChange: handleTableChange,
    scroll: {
      x: 'max-content'
    },
    className: 'w-full'
  };

  const searchFormProps: FormProps = {
    form,
    layout: 'inline',
    onFinish: handleSearch
  };

  return (
    <ListPageLayout tableProps={tableProps} searchFormProps={searchFormProps} />
  );
};
